import React from "react"
import Table from "material-ui/Table"
import TableHeader from "material-ui/Table/TableHeader"
import TableBody from "material-ui/Table/TableBody"
import EmployeeTimecardTableHeader from "./EmployeeTimecardTableHeader"
import EmployeeTimecardTableRow from "./EmployeeTimecardTableRow"
import EmployeeTimecardCalculatedValuesRow from "./EmployeeTimecardCalculatedValuesRow"
import EmployeeTimecardExpensesValuesRow from "./expenses/EmployeeTimecardExpensesValuesRow"
import EmployeeTimecardTotalsRow from "./EmployeeTimecardTotalsRow"
import EmployeeTimecardTotalAmountsRow from "./EmployeeTimecardTotalAmountsRow"
import EmployeeTimecardGrandTotalRow from "./EmployeeTimecardGrandTotalRow"
import {
    IPayrollPayGroupData, IPayrollPayCalendar, IJobStatusData, IPayrollStatusDocument, ELoggedInUserType,
    ITimecardWeek, IObjectTimecardDocument, ITimecardDay, ITimecardEmployee, IDate, ISuspensePunchData,
    IPunchSetDocument, ICommentData
} from "../../models"
import AccountStore from "../../stores/AccountStore"
import EmployeeTimecardExpensesHeaderRow from "./expenses/EmployeeTimecardExpensesHeaderRow"
import AuthenticationStore from "../../stores/AuthenticationStore"
import * as permissionHelper from "../../helpers/permissionHelper"

interface IProps {
    dailyNoteDates: IDate[]
    timecardWeek: ITimecardWeek
    payrollDataForPayGroup: IPayrollPayGroupData | IPayrollPayCalendar
    lockData: IPayrollStatusDocument[]
    employee: ITimecardEmployee
    timecard: IObjectTimecardDocument
    readonly: boolean
    hierarchyItemId: string
    jobId: string
    phaseId: string
    positionId: string
    payrollHierarchyId: string
    payrollPayGroupId: string
    suspensePunchData?: ISuspensePunchData
    jobStatusData?: IJobStatusData[]
    commentData?: ICommentData[]
    loggedInUserType?: ELoggedInUserType
    filterByPunchHierarchy?: boolean
    onAddPunchToTimecardDay(timecardDay: ITimecardDay, user: ITimecardEmployee, timecard: IObjectTimecardDocument, hierarchyItemId: string, positionId: string, payClassId: string, payTypeId: string, shiftId: string, breakId: string, mobileBreakId: string, jobId: string, phaseId: string, equipmentId: string, isPunchIn: boolean, pairedPunchId: string): void
    onTimeChange(punchId: string, dateTime: Date | string, errorCb: () => void): void
    onConfirmDelete(punchId: string): void
    reloadTimecard(msg: string): void
    handleApproveStatus(status: string, dayOfWeekIndex: number): void
    openDailyHistoryDrawer(date: number): void
    showExpenseRow: boolean
    handleApprovePunchSet?(status: "unapprove" | "approve", punchSet: IPunchSetDocument): void
    //we need to select hierarchy based on employee punch permission, not logged in user punch permission (when the user has more punch rights than the admin)
    //it's possible when full timecard, for example, employee->timecard and payroll->timecard
    //timecards page is not full timecard in the case filter by punch level
    isFullTimecard?: boolean

    //we need these value for PriorPeriodAdjustment
    priorPeriodAdjustMode?: boolean
    allowPriorPeriodAdjust?: boolean

    //if timecard is original on PriorPeriodAdjustment, we should show original punch values on punch detail dialog.
    originalTimecard?: boolean

    virtualTimecard?: boolean
    handleOpenVirtualTimecardPopup?(): void

    // if Job->Timecard, it's not full timecard object. so let's hide Info icon to show Notes, Audits and WorkHistory.
    hideInfoIcon?: boolean
    showTimecardDetails: boolean
}

const TimecardWeekTable: React.SFC<IProps> = (props: IProps) => {
    const differenceTimecard = props.timecard.PriorPeriodData && props.timecard.PriorPeriodData.DifferenceTimecard
    return (
        <div style={{ marginBottom: (differenceTimecard ? 50 : 0) }}>
            <Table selectable={false}>
                <TableHeader>
                    <EmployeeTimecardTableHeader
                        userId={props.employee.Id}
                        priorPeriodAdjustMode={props.priorPeriodAdjustMode}
                        timecardPriorPeriodData={props.timecard.PriorPeriodData}
                        fullPunchSets={props.timecard.MasterTimecard.FullPunchSets}
                        dailyNoteDates={props.dailyNoteDates}
                        filterByPunchHierarchy={props.filterByPunchHierarchy}
                        timecardDays={props.timecardWeek.Days}
                        lockData={props.lockData}
                        openDailyHistoryDrawer={props.openDailyHistoryDrawer}
                        handleApproveStatus={props.handleApproveStatus}
                        loggedInUserType={props.loggedInUserType}
                        allowPriorPeriodAdjust={props.allowPriorPeriodAdjust}
                        suspensePunchData={props.suspensePunchData}
                        virtualTimecard={props.virtualTimecard}
                        handleOpenVirtualTimecardPopup={props.handleOpenVirtualTimecardPopup}
                        hideInfoIcon={props.hideInfoIcon}
                    />
                </TableHeader>
                <TableBody showRowHover={false} deselectOnClickaway={false}>
                    {!differenceTimecard &&
                        <EmployeeTimecardTableRow
                            timecard={props.timecard}
                            lockData={props.lockData}
                            user={props.employee}
                            timecardDays={props.timecardWeek.Days}
                            addPunch={props.onAddPunchToTimecardDay}
                            onTimeChange={props.onTimeChange}
                            onConfirmDelete={props.onConfirmDelete}
                            readonly={props.readonly}
                            hierarchyItemId={props.hierarchyItemId}
                            jobId={props.jobId}
                            phaseId={props.phaseId}
                            positionId={props.positionId}
                            reloadTimecard={props.reloadTimecard}
                            isFullTimecard={props.isFullTimecard}
                            priorPeriodAdjustMode={props.priorPeriodAdjustMode}
                            allowPriorPeriodAdjust={props.allowPriorPeriodAdjust}
                            originalTimecard={props.originalTimecard}
                            suspensePunchData={props.suspensePunchData}
                            jobStatusData={props.jobStatusData}
                            commentData={props.commentData}
                            virtualTimecard={props.virtualTimecard}
                            handleApprovePunchSet={props.handleApprovePunchSet}
                            showTimecardDetails={props.showTimecardDetails}
                        />
                    }
                    {!differenceTimecard &&
                        <EmployeeTimecardCalculatedValuesRow
                            fullPunchSets={props.timecard.MasterTimecard.FullPunchSets}
                            timecardWeek={props.timecardWeek}
                            user={props.employee}
                            showTimecardDetails={props.showTimecardDetails}
                            noOps={props.timecard.PunchRuleNoOperationLogs}
                            punchRuleProperties={props.timecard.PunchRuleProperties}
                            reloadTimecard={props.reloadTimecard}
                        />
                    }
                    {AccountStore.getTimecardTimeExpensesEnabled() && props.showExpenseRow &&
                        <EmployeeTimecardExpensesHeaderRow
                            differenceTimecard={differenceTimecard}
                            timecardDays={props.timecardWeek.Days}
                            timecardPriorPeriodData={props.timecard.PriorPeriodData}
                        />
                    }
                    {AccountStore.getTimecardTimeExpensesEnabled() && props.showExpenseRow &&
                        <EmployeeTimecardExpensesValuesRow
                            timecardPriorPeriodData={props.timecard.PriorPeriodData}
                            loggedInUserType={props.loggedInUserType}
                            payrollDataForPayGroup={props.payrollDataForPayGroup}
                            readonly={props.readonly}
                            timecardWeek={props.timecardWeek}
                            user={props.employee}
                            lockData={props.lockData}
                            reloadTimecard={props.reloadTimecard}
                            priorPeriodAdjustMode={props.priorPeriodAdjustMode}
                            allowPriorPeriodAdjust={props.allowPriorPeriodAdjust}
                            hierarchyItemId={props.hierarchyItemId}
                            jobId={props.jobId}
                            phaseId={props.phaseId}
                            positionId={props.positionId}
                            payrollHierarchyId={props.payrollHierarchyId}
                            payrollPayGroupId={props.payrollPayGroupId}
                        />
                    }
                    {!differenceTimecard &&
                        <EmployeeTimecardTotalsRow
                            userId={props.employee.Id}
                            timecardPriorPeriodData={props.timecard.PriorPeriodData}
                            filterByPunchHierarchy={props.filterByPunchHierarchy}
                            timecardDays={props.timecardWeek.Days}
                            lockData={props.lockData}
                            priorPeriodAdjustMode={props.priorPeriodAdjustMode}
                            allowPriorPeriodAdjust={props.allowPriorPeriodAdjust}
                        />
                    }
                    {AccountStore.getRateTotalsEnabled() && permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.ViewRates) && !differenceTimecard &&
                        <EmployeeTimecardTotalAmountsRow
                            userId={props.employee.Id}
                            timecardPriorPeriodData={props.timecard.PriorPeriodData}
                            timecardDays={props.timecardWeek.Days}
                            lockData={props.lockData}
                            priorPeriodAdjustMode={props.priorPeriodAdjustMode}
                            allowPriorPeriodAdjust={props.allowPriorPeriodAdjust}
                            loggedInUserType={props.loggedInUserType}
                        />
                    }
                </TableBody>
            </Table>
            {!differenceTimecard &&
                <EmployeeTimecardGrandTotalRow
                    filterByPunchHierarchy={props.filterByPunchHierarchy}
                    timecardWeek={props.timecardWeek}
                    timecardPriorPeriodData={props.timecard.PriorPeriodData}
                    priorPeriodAdjustMode={props.priorPeriodAdjustMode}
                    allowPriorPeriodAdjust={props.allowPriorPeriodAdjust}
                    loggedInUserType={props.loggedInUserType}
                    blendedOvertimeRate={props.timecard.BlendedOvertimeRate}
                />
            }
        </div>
    )
}

export default TimecardWeekTable